import React, { useContext, useEffect, useRef, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import createApp from '@shopify/app-bridge';
import { NavigationMenu, AppLink } from '@shopify/app-bridge/actions';
import { AppBridgeConfigContext } from './AppBridgeProvider';

/**
 * NavigationMenu Component
 * Implements NavigationMenu using App Bridge actions API
 * Follows the Shopify App Bridge NavigationMenu documentation:
 * https://shopify.dev/docs/api/app-bridge/previous-versions/actions/menu/navigation#react
 */
export default function NavigationMenuComponent({ navigationLinks, matcher }) {
  const location = useLocation();
  const navigate = useNavigate();
  const config = useContext(AppBridgeConfigContext);
  const navigationMenuRef = useRef(null);
  const appRef = useRef(null);
  const appLinksRef = useRef([]);

  // Default navigation links if not provided
  const defaultNavigationLinks = useMemo(() => [
    {
      label: 'Widget',
      destination: '/app/widget',
    },
    {
      label: 'Settings',
      destination: '/app/settings',
    },
  ], []);

  const links = navigationLinks || defaultNavigationLinks;

  // Default matcher function if not provided
  const defaultMatcher = useMemo(() => {
    return (link, currentLocation) => {
      return currentLocation.pathname === link.destination || 
             (link.destination !== '/app' && currentLocation.pathname.startsWith(link.destination));
    };
  }, []);

  const matchFunction = matcher || defaultMatcher;

  useEffect(() => {
    // Only initialize if we have config
    if (!config || !config.apiKey || !config.host) {
      return;
    }

    // Create App Bridge app instance
    const appBridgeConfig = {
      apiKey: config.apiKey,
      host: config.host,
    };

    try {
      // Create app if not already created
      if (!appRef.current) {
        appRef.current = createApp(appBridgeConfig);
      }

      const app = appRef.current;

      // Create AppLink instances for each navigation link
      appLinksRef.current = links.map(link => {
        // Use destination as-is - App Bridge will preserve query parameters automatically
        return AppLink.create(app, {
          label: link.label,
          destination: link.destination,
        });
      });

      // Create NavigationMenu with AppLinks
      navigationMenuRef.current = NavigationMenu.create(app, {
        items: appLinksRef.current,
      });

      // Set active link based on current location
      const activeLink = appLinksRef.current.find((link, index) => 
        matchFunction(links[index], location)
      );

      if (activeLink) {
        navigationMenuRef.current.set({ active: activeLink });
      }

      // Handle navigation when links are clicked
      // App Bridge will handle the navigation, but we need to sync with React Router
      const handleNavigation = () => {
        // Listen for route changes and update active state
        const currentActiveLink = appLinksRef.current.find((link, index) => 
          matchFunction(links[index], location)
        );

        if (currentActiveLink && navigationMenuRef.current) {
          navigationMenuRef.current.set({ active: currentActiveLink });
        }
      };

      // Update active state when location changes
      handleNavigation();

    } catch (error) {
      console.error('Error initializing NavigationMenu:', error);
    }

    // Cleanup
    return () => {
      if (navigationMenuRef.current) {
        try {
          navigationMenuRef.current.unsubscribe();
        } catch (e) {
          // Ignore cleanup errors
        }
      }
    };
  }, [config, links, location, matchFunction]);

  // Update active link when location changes
  useEffect(() => {
    if (navigationMenuRef.current && appLinksRef.current.length > 0) {
      const activeLink = appLinksRef.current.find((link, index) => 
        matchFunction(links[index], location)
      );

      if (activeLink) {
        navigationMenuRef.current.set({ active: activeLink });
      } else {
        navigationMenuRef.current.set({ active: undefined });
      }
    }
  }, [location, links, matchFunction]);

  // Handle navigation to work with React Router
  // App Bridge NavigationMenu will navigate using browser, we need to sync with React Router
  useEffect(() => {
    let lastPath = window.location.pathname + window.location.search;

    const checkNavigation = () => {
      const currentPath = window.location.pathname + window.location.search;
      if (currentPath !== lastPath) {
        lastPath = currentPath;
        // Sync React Router with browser navigation
        navigate(window.location.pathname + window.location.search, { replace: true });
      }
    };

    // Check for navigation changes periodically
    const interval = setInterval(checkNavigation, 100);

    // Also listen for popstate events
    const handlePopState = () => {
      checkNavigation();
    };

    window.addEventListener('popstate', handlePopState);

    return () => {
      clearInterval(interval);
      window.removeEventListener('popstate', handlePopState);
    };
  }, [navigate]);

  // This component doesn't render anything visible
  // NavigationMenu is rendered by App Bridge in the Shopify Admin sidebar
  return null;
}
